import React, { useEffect, useState, useRef, useCallback } from 'react';
import { IoIosClose } from 'react-icons/io';
import { FaStar } from 'react-icons/fa';
import Button from 'components/ui/button';
import { registerInteractionCampaign, verifyCampaign } from 'services/actionlog.service';
import { useAuth } from 'context/AuthContext';
import TextEditor from 'components/text-editor';

interface FeedbackModalProps {
  onClose: () => void;
}

const tooltips = ['Muito insatisfeito', 'Insatisfeito', 'Neutro', 'Satisfeito', 'Excelente'];

const FOCUSABLE_SELECTORS = [
  'button:not([disabled])',
  'input:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
  'a[href]',
  '[contenteditable="true"]',
].join(', ');

const FeedbackCampaignModal: React.FC<FeedbackModalProps> = ({ onClose }) => {
  const { user } = useAuth();

  const [rating, setRating] = useState<number>(0);
  const [hover, setHover] = useState<number | null>(null);
  const [comment, setComment] = useState('');
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [feedbackSent, setFeedbackSent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const modalRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const previousActiveElement = useRef<HTMLElement | null>(null);

  const handleSubmit = async () => {
    if ((rating === 0 && !dontShowAgain) || isLoading) return;

    setIsLoading(true);

    const payload = dontShowAgain ? {} : { rating, comment };

    sendActionLog(payload);

    setFeedbackSent(true);
    setIsLoading(false);

    setTimeout(() => {
      handleClose();
    }, 3000);
  };

  function sendActionLog(payload: object) {
    registerInteractionCampaign('me', 'evaluate_end_of_year_action', payload, null, null, null, false);
  }

  const handleClose = useCallback(() => {
    setRating(0);
    setHover(null);
    setComment('');
    setDontShowAgain(false);
    setFeedbackSent(false);
    setIsLoading(false);
    setShowModal(false);

    // Restaurar foco ao elemento anterior
    if (previousActiveElement.current) {
      previousActiveElement.current.focus();
    }

    onClose();
  }, [onClose]);

  // Focus trap
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleClose();
        return;
      }

      if (event.key !== 'Tab' || !modalRef.current) return;

      const focusableElements = modalRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTORS);
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey) {
        if (document.activeElement === firstElement) {
          event.preventDefault();
          lastElement?.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          event.preventDefault();
          firstElement?.focus();
        }
      }
    },
    [handleClose],
  );

  // Setup focus trap e eventos
  useEffect(() => {
    if (!showModal) return;

    // Salvar elemento focado anteriormente
    previousActiveElement.current = document.activeElement as HTMLElement;

    // Bloquear scroll do body
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // Adicionar listener de teclado
    document.addEventListener('keydown', handleKeyDown);

    // Focar no botão de fechar
    setTimeout(() => {
      closeButtonRef.current?.focus();
    }, 0);

    return () => {
      document.body.style.overflow = originalOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showModal, handleKeyDown]);

  useEffect(() => {
    async function checkShowModal() {
      const result = await verifyCampaign('evaluate_end_of_year_action');
      const alreadySeen = Array.isArray(result) && result.length >= 1;
      setShowModal(!alreadySeen);
    }

    if (user) {
      checkShowModal();
    }
  }, [user]);

  // Navegação por teclado nas estrelas
  const handleStarKeyDown = (event: React.KeyboardEvent, star: number) => {
    if (event.key === 'ArrowRight' || event.key === 'ArrowUp') {
      event.preventDefault();
      const nextStar = star < 5 ? star + 1 : 1;
      setRating(nextStar);
      const nextButton = document.querySelector(`[data-star="${nextStar}"]`) as HTMLElement;
      nextButton?.focus();
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') {
      event.preventDefault();
      const prevStar = star > 1 ? star - 1 : 5;
      setRating(prevStar);
      const prevButton = document.querySelector(`[data-star="${prevStar}"]`) as HTMLElement;
      prevButton?.focus();
    }
  };

  if (!showModal) return null;

  const modalTitleId = 'feedback-modal-title';
  const modalDescId = 'feedback-modal-description';

  return (
    <div className="container-avaliacao-campanha">
      <div
        className="modal-overlay fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50"
        role="presentation"
        onClick={handleClose}
        aria-hidden="true"
      />
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={modalTitleId}
        aria-describedby={modalDescId}
        className="fixed inset-0 z-50 overflow-y-auto pointer-events-none"
      >
        <div className="flex min-h-full items-center justify-center p-4">
          <div
            className="relative bg-oxford-blue border-electric-blue rounded-3xl p-8 shadow-2xl max-w-[600px] w-full text-center border-4 text-white pointer-events-auto"
            onClick={e => e.stopPropagation()}
          >
          <button
            ref={closeButtonRef}
            type="button"
            className="bg-purple text-white absolute -right-4 -top-4 w-10 h-10 flex items-center justify-center rounded-full hover:ring hover:ring-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-yellow shadow-xl font-bold"
            onClick={handleClose}
            aria-label="Fechar modal de avaliação"
          >
            <IoIosClose size={28} color="white" aria-hidden="true" />
          </button>

          {!feedbackSent ? (
            <div className='space-y-7'>
              <h2 id={modalTitleId} className="text-2xl font-bold mb-4 text-center text-yellow">
                Sua opinião é importante!
              </h2>
              <p id={modalDescId} className="text-center text-sm ">
                Nos ajude a melhorar avaliando sua satisfação com <br /> as ações de engajamento de fim de ano:
              </p>

              <ul className="flex flex-wrap justify-center gap-2 " aria-label="Ações avaliadas">
                <li className="text-yellow font-bold text-sm px-3 py-1 bg-white bg-opacity-10 rounded-full">
                  Retrospectiva 2025
                </li>
                <li className="text-yellow font-bold text-sm px-3 py-1 bg-white bg-opacity-10 rounded-full">
                  Quiz de Fim de Ano
                </li>
                <li className="text-yellow font-bold text-sm px-3 py-1 bg-white bg-opacity-10 rounded-full">Pontos em Dobro</li>
              </ul>

              <div
                role="radiogroup"
                aria-label={`Avaliação: ${rating > 0 ? `${rating} de 5 estrelas - ${tooltips[rating - 1]}` : 'Nenhuma estrela selecionada'}`}
                className="flex justify-center space-x-2"
              >
                {[1, 2, 3, 4, 5].map(star => (
                  <button
                    key={star}
                    type="button"
                    role="radio"
                    aria-checked={rating === star}
                    aria-label={`${star} ${star === 1 ? 'estrela' : 'estrelas'} - ${tooltips[star - 1]}`}
                    data-star={star}
                    onClick={() => setRating(star)}
                    onKeyDown={e => handleStarKeyDown(e, star)}
                    onMouseEnter={() => setHover(star)}
                    onMouseLeave={() => setHover(null)}
                    className="relative focus:outline-none focus-visible:ring-2 focus-visible:ring-yellow focus-visible:ring-offset-2 focus-visible:ring-offset-oxford-blue rounded"
                    disabled={isLoading}
                    tabIndex={rating === star || (rating === 0 && star === 1) ? 0 : -1}
                  >
                    <FaStar size={32} color={(hover ?? rating) >= star ? '#FFD700' : '#d1d5db'} aria-hidden="true" />
                    {(hover ?? rating) === star && (
                      <span
                        role="tooltip"
                        className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-black text-white text-xs rounded px-2 py-1 whitespace-nowrap"
                      >
                        {tooltips[star - 1]}
                      </span>
                    )}
                  </button>
                ))}
              </div>

              <div className="text-white text-left">
                <label htmlFor="feedback-comment" className="mb-2 block">
                  Tem algum comentário ou sugestão? (opcional)
                </label>
                <div className="h-28 w-full bg-transparent text-xs font-thin">
                  <TextEditor disableEmoji onChange={html => setComment(html)} ariaLabel="Comentário ou sugestão (opcional)" />
                </div>
              </div>

              <div className="flex items-center mb-6">
                <input
                  type="checkbox"
                  id="dontShowAgain"
                  checked={dontShowAgain}
                  onChange={() => setDontShowAgain(!dontShowAgain)}
                  className="mr-2 w-4 h-4 cursor-pointer"
                  disabled={isLoading}
                />
                <label htmlFor="dontShowAgain" className="text-sm cursor-pointer">
                  Não abrir mais essa mensagem.
                </label>
              </div>

              <Button
                color="primary"
                className="w-full font-bold py-3 rounded-md text-white hover:bg-blue-700 disabled:opacity-50 flex justify-center items-center"
                onClick={handleSubmit}
                disabled={(rating === 0 && !dontShowAgain) || isLoading}
                aria-busy={isLoading}
              >
                {isLoading ? (
                  <span className="animate-pulse" aria-live="polite">
                    Enviando...
                  </span>
                ) : !dontShowAgain || rating > 0 ? (
                  'Enviar avaliação'
                ) : (
                  'Desejo não participar!'
                )}
              </Button>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center p-8" role="status" aria-live="polite">
              <h2 id={modalTitleId} className="text-2xl font-bold text-yellow mb-4">
                Obrigado pela sua avaliação!
              </h2>
              <p className="text-center">Seu feedback é muito importante para nós.</p>
            </div>
          )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeedbackCampaignModal;
